import { useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import ControlSelect from "~app/ControlSelect"
import { createDropdownCollection } from "~utils/dropdown"
import useUserBrokerConnections from "~hooks/useUserBrokerConnections"
import FlexRow from "~components/FlexRow"
import FlexCol from "~components/FlexCol"
import { ButtonPrimarySoftLarge } from "~styled/button"
import { BrokerService } from "~services/BrokerService"
import { useMutation } from "react-query"
import { toast } from "react-toastify"
import { useQueryClient } from "react-query"
import { BROKER_CONNECTION_STATE } from "~shapes/portfolio"
interface Props {
  userId: string
}

const BrokerConnection = ({ userId }: Props) => {
  const methods = useForm()
  const { control, watch, getValues, reset } = methods
  const queryClient = useQueryClient()
  const watchedBroker = watch("selected_broker")
  const {
    data,
    refetch,
    isLoading,
    isError: isErrorGettingBrokerConn,
  } = useUserBrokerConnections(userId)
  const { mutateAsync: disconnectBroker, isLoading: isDisconnecting } =
    useMutation(
      (brokerId: string) =>
        BrokerService.disconnectUserFromBrokers(userId, brokerId),
      {
        onSuccess: async () => {
          toast.success("Broker disconnected successfully")
          await queryClient.invalidateQueries([
            "user-broker-connections",
            userId,
          ])
          const newData: any[] = await refetch().then(r => r.data)

          const firstValid = newData?.find(
            p => p.state === BROKER_CONNECTION_STATE.AUTHORIZED
          )
          if (firstValid?.broker) {
            const newBroker = {
              label: firstValid.broker,
              value: firstValid.id,
            }

            methods.reset({
              ...methods.getValues(),
              selected_broker: newBroker,
            })
          } else {
            methods.reset({
              ...methods.getValues(),
              selected_broker: null,
            })
          }
        },
        onError: () => {
          toast.error("Failed to disconnect broker")
        },
      }
    )

  useEffect(() => {
    if (userId) {
      ;(async () => {
        await refetch()
      })()
    }
  }, [userId])

  useEffect(() => {
    if (isErrorGettingBrokerConn) {
      toast.error("There was an error getting the broker connections")
    }
  }, [isErrorGettingBrokerConn])

  useEffect(() => {
    if (data) {
      if (data?.length > 0) {
        const defaultBroker = data[0]?.broker
        if (!watchedBroker?.value && defaultBroker) {
          const defaultOption = { label: data[0].broker, value: data[0].id }
          if (getValues("selected_broker")?.value !== defaultBroker) {
            reset({
              ...getValues(),
              selected_broker: defaultOption,
            })
          }
        }
      }
    }
  }, [data])

  const createBrokerDropdown = () => {
    const options = data?.map(p => ({
      label: p.broker,
      name: p.id,
    }))
    return createDropdownCollection(options, "label", "name")
  }

  const handleDisconnectBroker = async () => {
    if (watchedBroker.value) {
      if (
        confirm(
          "This action can't be reverted, are you sure you want to disconnect this broker?"
        )
      ) {
        await disconnectBroker(watchedBroker.value)
      }
    }
  }

  if (isLoading) {
    return (
      <div style={{ marginTop: 20, textAlign: "center" }}>
        <p>Loading broker connections data...</p>
      </div>
    )
  }

  if (!data?.length) {
    return (
      <div style={{ marginTop: 20, textAlign: "center" }}>
        <p>This user does not have broker connections</p>
      </div>
    )
  }

  return (
    <>
      <FormProvider {...methods}>
        <FlexRow style={{ alignItems: "center", gap: 10 }}>
          <FlexCol>
            <ControlSelect
              name="selected_broker"
              label="Broker"
              control={control}
              rules={{ required: true }}
              options={createBrokerDropdown()}
              isMulti={false}
              isLoading={isLoading}
            />
          </FlexCol>

          <FlexCol
            style={{
              marginBottom: 10,
              justifyContent: "center",
              display: "flex",
            }}
          >
            <ButtonPrimarySoftLarge
              onClick={handleDisconnectBroker}
              disabled={false}
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect broker"}
            </ButtonPrimarySoftLarge>
          </FlexCol>
        </FlexRow>
      </FormProvider>
    </>
  )
}

export default BrokerConnection
